<template>
  <section v-if="levels.length" class="demand">
    <h3 class="demand__title">Demand by {{ rateBased ? "Rate" : "Price" }}</h3>
    <p class="demand__lead">
      How much has been bid at each {{ rateBased ? "lowest rate" : "top price" }}.
      Most demand is at {{ levelLabel(top.level) }} so far.
    </p>
    <div class="demand__bars" role="img" :aria-label="`Bids at each ${rateBased ? 'rate' : 'price'}; most at ${levelLabel(top.level)}`">
      <div v-for="level in levels" :key="level.level" class="demand__col">
        <span class="demand__amount">{{ short(level.amount_at) }}</span>
        <span class="demand__bar-track">
          <span class="demand__bar" :class="{ 'demand__bar--top': level.level === top.level }" :style="{ height: `${Math.max(4, (level.amount_at / maxAmount) * 100)}%` }"></span>
        </span>
        <span class="demand__level">{{ levelLabel(level.level) }}</span>
      </div>
    </div>
    <div class="demand__stats">
      <div>
        <span class="demand__label">Most Bid At</span>
        <span class="demand__value">{{ levelLabel(top.level) }}</span>
        <span class="demand__sub">{{ rateBased ? "a year" : "a share" }}</span>
      </div>
      <div>
        <span class="demand__label">Bid At {{ levelLabel(top.level) }} or {{ rateBased ? "Less" : "More" }}</span>
        <span class="demand__value">{{ short(top.amount_cumulative) }}</span>
      </div>
      <div>
        <span class="demand__label">{{ stillNeeded > 0 ? "Still Needed" : "Covered" }}</span>
        <span class="demand__value">{{ stillNeeded > 0 ? short(stillNeeded) : `${coverPercent}%` }}</span>
        <span class="demand__sub">{{ stillNeeded > 0 ? `to reach ${short(raiseAmount)}` : `of the ${short(raiseAmount)} raise` }}</span>
      </div>
    </div>
  </section>
</template>

<script>
import { compact, currencySymbol, money } from "@/utils/exchange.js";

/**
 * Demand at each level of an open book (GET /token/:id/book): how much has been bid at each top
 * price (equity) or lowest rate (a bond), the level with the most demand, how much is bid at it
 * or better, and what is still needed to fill the raise. The ladder's cumulative counts strike
 * bids at every level, as the backend explains, so "or more" includes them.
 */
export default {
  name: "DemandByPrice",
  props: {
    book: { type: Object, default: null },
    currency: { type: String, default: "NGN" },
  },
  computed: {
    rateBased() { return Boolean(this.book?.offering?.is_rate_based); },
    // Low to high, the way a price axis reads; the backend sends best first.
    levels() {
      return [...(this.book?.ladder || [])]
        .filter((l) => Number(l.amount_at) > 0)
        .sort((a, b) => Number(a.level) - Number(b.level));
    },
    top() { return this.levels.reduce((best, l) => (Number(l.amount_at) > Number(best.amount_at) ? l : best), this.levels[0]); },
    maxAmount() { return Math.max(...this.levels.map((l) => Number(l.amount_at)), 1); },
    raiseAmount() { return Number(this.book?.offering?.raise_amount) || 0; },
    totalDemand() { return Number(this.book?.summary?.total_demand) || 0; },
    stillNeeded() { return Math.max(0, this.raiseAmount - this.totalDemand); },
    coverPercent() { return this.raiseAmount > 0 ? Math.round((this.totalDemand / this.raiseAmount) * 100) : 0; },
  },
  methods: {
    levelLabel(level) {
      return this.rateBased ? `${Number(level)}%` : money(level, this.currency, Number(level) % 1 ? 2 : 0);
    },
    short(value) {
      return `${currencySymbol(this.currency)}${compact(Number(value) || 0)}`;
    },
  },
};
</script>

<style lang="scss" scoped>
$text-primary: #0f172a;
$text-secondary: #475569;
$text-muted: #94a3b8;
$border-color: #e2e8f0;
$accent: #4d84ff;
$btn-primary: #001240;

.demand {
  background: #ffffff;
  border: 1px solid $border-color;
  border-radius: 12px;
  padding: 20px;

  &__title { font-size: 15px; font-weight: 600; color: $text-primary; margin: 0 0 4px; }
  &__lead { font-size: 13px; color: $text-secondary; margin: 0 0 16px; }

  &__bars {
    display: flex;
    align-items: stretch;
    gap: 8px;
    height: 180px;
    overflow-x: auto;
  }

  &__col {
    flex: 1 0 44px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 6px;
    min-width: 44px;
  }

  &__amount { font-size: 11px; color: $text-secondary; }

  &__bar-track {
    flex: 1;
    width: 100%;
    display: flex;
    align-items: flex-end;
  }

  &__bar {
    width: 100%;
    border-radius: 6px 6px 2px 2px;
    background: rgba(77, 132, 255, 0.35);
    &--top { background: $btn-primary; }
  }

  &__level { font-size: 12px; font-weight: 600; color: $text-primary; }

  &__stats {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 12px;
    border-top: 1px solid $border-color;
    margin-top: 14px;
    padding-top: 14px;
    > div { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
  }

  &__label { font-size: 12px; color: $text-secondary; }
  &__value { font-size: 18px; font-weight: 600; color: $text-primary; }
  &__sub { font-size: 12px; color: $text-muted; }
}
</style>
